import { useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { AssetTreeState, getAssetTreeSelection } from '@/configuration/assetTree/assetTreeSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { MaintenanceDataRequestParams, useSearchMaintenanceDataQuery } from '@/services/vehicleDiagnosticsApi';
import { useMaintenanceDataQueryParams } from './useMaintenanceDataQueryParams';

export const useMaintenanceDataRequestData = (initialParams: Partial<MaintenanceDataRequestParams>) => {
    const { workshopIds, assetIds, accountIds, initialized }: AssetTreeState = useAppSelector(getAssetTreeSelection);
    const params = useMaintenanceDataQueryParams({ ...initialParams, workshopIds, assetIds, accountIds });
    const [shouldSkipFetching, setShouldSkipFetching] = useState(true);

    useEffect(() => {
        if (initialized && params.settingsReady) {
            params.setAssetIds(assetIds);
            params.setWorkshopIds(workshopIds);
            params.setAccountIds(accountIds);
            params.pagination.setPageNumber(0);
            setShouldSkipFetching(false);
        }
    }, [assetIds, workshopIds, accountIds, initialized, params.settingsReady]);

    const queryParams: MaintenanceDataRequestParams = useMemo(
        () => ({
            pageSize: params.pagination.pageSize,
            pageNumber: params.pagination.pageNumber,
            sortBy: params.sorting.sortBy,
            sortDirection: params.sorting.sortDirection,
            assetIds: params.assetIds,
            workshopIds: params.workshopIds,
            accountIds: params.accountIds,
            status: params.status,
            componentType: params.componentType,
            component: params.component,
            dueDateFrom: params.dueDateFrom,
            dueDateTo: params.dueDateTo,
            dueMileageMin: params.dueMileageMin,
            dueMileageMax: params.dueMileageMax,
            mileageMin: params.mileageMin,
            mileageMax: params.mileageMax,
            dueRangesOperator: params.dueRangesOperator,
            activeMaintenance: params.activeMaintenance,
            solvedByCustomer: params.solvedByCustomer,
        }),
        [
            params.pagination.pageSize,
            params.pagination.pageNumber,
            params.sorting.sortBy,
            params.sorting.sortDirection,
            params.assetIds,
            params.workshopIds,
            params.accountIds,
            params.status,
            params.componentType,
            params.component,
            params.dueDateFrom,
            params.dueDateTo,
            params.dueMileageMin,
            params.dueMileageMax,
            params.mileageMin,
            params.mileageMax,
            params.dueRangesOperator,
            params.activeMaintenance,
            params.solvedByCustomer,
        ],
    );
    const query = useSearchMaintenanceDataQuery(shouldSkipFetching ? skipToken : queryParams);

    // `query` is spread first so the filter fields from `params` (notably `status`) win over
    // RTK Query's own `status` field, which this hook's consumers do not use. `queryParams` is
    // exposed so consumers (e.g. CSV export) can reuse the exact request shape without
    // re-listing every field.
    return { ...query, ...params, queryParams };
};
